import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Scissors, Lock, Mail } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { resetSessionExpiredEmitter } from '../utils/sessionExpired';
import './Login.css';

const Login = () => {
    const [accountType, setAccountType] = useState('barbero');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [mounted, setMounted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotMessage, setForgotMessage] = useState('');
    const [forgotError, setForgotError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        setMounted(true);
        // Check if already logged in
        const token = localStorage.getItem('token');
        const userRole = localStorage.getItem('userRole');
        if (token && userRole) {
            if (userRole === 'admin') {
                navigate('/admin');
            } else if (userRole === 'barber') {
                navigate('/barber');
            } else if (userRole === 'client') {
                navigate('/client');
            } else {
                navigate('/');
            }
        }
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email.trim(),
                    password: password.trim(),
                }),
            });

            const data = await response.json();

            if (response.ok) {
                // Store token
                localStorage.setItem('token', data.access_token);
                resetSessionExpiredEmitter();

                // Get user info to determine real role
                try {
                    const userInfoResponse = await fetch(`${API_BASE_URL}/auth/me`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${data.access_token}`,
                            'Content-Type': 'application/json',
                        },
                    });

                    if (userInfoResponse.ok) {
                        const userInfo = await userInfoResponse.json();
                        const userRole = userInfo.role || 'barber';

                        // Store real role from backend
                        localStorage.setItem('userRole', userRole);

                        // Redirect based on actual role from backend
                        if (userRole === 'admin') {
                            navigate('/admin');
                        } else if (userRole === 'barber') {
                            navigate('/barber');
                        } else if (userRole === 'client') {
                            navigate('/client');
                        } else {
                            navigate('/');
                        }
                    } else {
                        // Fallback: decode JWT token if /auth/me fails
                        const tokenParts = data.access_token.split('.');
                        if (tokenParts.length === 3) {
                            try {
                                const payload = JSON.parse(atob(tokenParts[1]));
                                const userRole = payload.role || 'barber';
                                localStorage.setItem('userRole', userRole);

                                if (userRole === 'admin') {
                                    navigate('/admin');
                                } else if (userRole === 'barber') {
                                    navigate('/barber');
                                } else if (userRole === 'client') {
                                    navigate('/client');
                                } else {
                                    navigate('/');
                                }
                            } catch (decodeError) {
                                console.error('Error decoding token:', decodeError);
                                setError('Error al obtener información del usuario');
                            }
                        }
                    }
                } catch (userInfoError) {
                    console.error('Error fetching user info:', userInfoError);
                    // Fallback: decode JWT token
                    const tokenParts = data.access_token.split('.');
                    if (tokenParts.length === 3) {
                        try {
                            const payload = JSON.parse(atob(tokenParts[1]));
                            const userRole = payload.role || 'barber';
                            localStorage.setItem('userRole', userRole);

                            if (userRole === 'admin') {
                                navigate('/admin');
                            } else if (userRole === 'barber') {
                                navigate('/barber');
                            } else if (userRole === 'client') {
                                navigate('/client');
                            } else {
                                navigate('/');
                            }
                        } catch (decodeError) {
                            console.error('Error decoding token:', decodeError);
                            setError('Error al obtener información del usuario');
                        }
                    }
                }
            } else {
                setError(data.detail || 'Error de autenticación');
            }
        } catch (error) {
            console.error('Login error:', error);
            setError('Error de conexión. Inténtalo de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    const openForgotModal = () => {
        setForgotEmail(email.trim() || '');
        setForgotMessage('');
        setForgotError('');
        setForgotOpen(true);
    };

    const closeForgotModal = () => {
        setForgotOpen(false);
        setForgotLoading(false);
        setForgotError('');
    };

    const parseDetail = (detail) => {
        if (!detail) return 'Error al enviar la solicitud';
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
        return 'Error al enviar la solicitud';
    };

    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        setForgotError('');
        setForgotMessage('');
        try {
            const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotEmail.trim() }),
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                setForgotMessage(data.message || 'Si el correo está registrado, recibirás un correo con una contraseña temporal.');
            } else {
                setForgotError(parseDetail(data.detail));
            }
        } catch {
            setForgotError('Error de conexión. Inténtalo de nuevo.');
        } finally {
            setForgotLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="background-animation"></div>

            <div className={`login-card ${mounted ? 'fade-in-up' : ''}`}>
                <Link to="/" className={`login-logo ${mounted ? 'fade-in-down' : ''}`}>
                    <div className="logo-icon">
                        <Scissors size={28} strokeWidth={2.5} />
                    </div>
                    <span className="logo-text">GENUINO'S</span>
                </Link>
                <div className="card-header">
                    <h1 className="login-title">Iniciar Sesión</h1>
                    <p className="login-subtitle">Accede a tu panel de control</p>
                </div>



                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="login-form">
                    <div className="form-group">
                        <label className="form-label">Correo electrónico</label>
                        <div className="login-input-wrapper">
                            <Mail className="login-input-icon" size={20} />
                            <input
                                type="email"
                                className="login-form-input"
                                placeholder="tu@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Contraseña</label>
                        <div className="login-input-wrapper">
                            <Lock className="login-input-icon" size={20} />
                            <input
                                type="password"
                                className="login-form-input"
                                placeholder="........"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>
                        <button
                            type="button"
                            className="forgot-password-link"
                            onClick={openForgotModal}
                            disabled={loading}
                        >
                            ¿Olvidaste tu contraseña?
                        </button>
                    </div>

                    <button type="submit" className="login-submit-btn" disabled={loading}>
                        <span>{loading ? 'Ingresando...' : 'Ingresar'}</span>
                        <div className="btn-ripple"></div>
                    </button>
                </form>

                <div className="login-footer">
                    <p>¿No tienes cuenta? <Link to="/register" className="register-link">Regístrate aquí</Link></p>
                </div>
            </div>

            {forgotOpen && (
                <div className="login-modal-overlay" role="presentation" onClick={closeForgotModal}>
                    <div
                        className="login-modal"
                        role="dialog"
                        aria-labelledby="forgot-modal-title"
                        onClick={(ev) => ev.stopPropagation()}
                    >
                        <h2 id="forgot-modal-title" className="login-modal-title">
                            Recuperar contraseña
                        </h2>
                        <p className="login-modal-text">
                            Escribe el correo con el que te registraste. Si existe una cuenta, te enviaremos una contraseña temporal.
                        </p>
                        {forgotError && <div className="error-message login-modal-alert">{forgotError}</div>}
                        {forgotMessage && <div className="login-modal-success">{forgotMessage}</div>}
                        <form onSubmit={handleForgotSubmit} className="login-modal-form">
                            <div className="form-group">
                                <label className="form-label">Correo electrónico</label>
                                <div className="login-input-wrapper">
                                    <Mail className="login-input-icon" size={20} />
                                    <input
                                        type="email"
                                        className="login-form-input"
                                        placeholder="tu@email.com"
                                        value={forgotEmail}
                                        onChange={(e) => setForgotEmail(e.target.value)}
                                        required
                                        disabled={forgotLoading}
                                    />
                                </div>
                            </div>
                            <div className="login-modal-actions">
                                <button
                                    type="button"
                                    className="login-modal-btn secondary"
                                    onClick={closeForgotModal}
                                    disabled={forgotLoading}
                                >
                                    Cerrar
                                </button>
                                <button type="submit" className="login-modal-btn primary" disabled={forgotLoading}>
                                    {forgotLoading ? 'Enviando...' : 'Enviar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Login;

